import { useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Navbar from '../components/navbar';
import { authApi } from '../api/authApi';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Please enter your email address.');
            return;
        }

        setSubmitting(true);
        try {
            await authApi.requestPasswordReset(email.trim());
            setSubmitted(true);
        } catch (err: any) {
            setError(err?.message || 'We could not send a reset link. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Navbar />
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
                        Reset your password
                    </Typography>

                    {submitted ? (
                        <>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                If an account exists for that email, we just sent a link to reset your password.
                                Please check your inbox (and spam folder).
                            </Alert>
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Button component={Link} to="/signin" variant="contained">
                                    Back to Sign In
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Enter the email address for your account and we'll send you a link to set a new password.
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={submitting}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 2, mb: 1, py: 1.5 }}
                                    disabled={submitting}
                                >
                                    {submitting ? <CircularProgress size={24} /> : 'Send reset link'}
                                </Button>
                            </Box>

                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Typography variant="body2">
                                    Remembered it?{' '}
                                    <Link to="/signin" style={{ color: 'inherit' }}>
                                        Back to Sign In
                                    </Link>
                                </Typography>
                            </Box>
                        </>
                    )}
                </Paper>
            </Container>
        </>
    );
}
